import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useLanguage } from "@/i18n/LanguageContext";
import { whatsappLink } from "@/lib/brand";

const navItems = (t) => [
  { label: t.nav.home, href: "/" },
  { label: t.nav.about, href: "/about" },
  { label: t.nav.services, href: "/services" },
  { label: t.nav.contact, href: "/contact" },
];

export default function Header() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current !== location.pathname) {
      lastPath.current = location.pathname;
      setOpen(false);
    }
  }, [location.pathname]);

  const items = navItems(t);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md bg-white/85 border-b border-brand-soft" : "bg-white"
      }`}
      data-testid="site-header"
    >
      <div className="container-brand flex items-center justify-between py-3.5">
        <Logo dataTestId="header-logo" />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {items.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-display font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-[color:var(--brand-primary)] bg-[color:var(--brand-surface-soft)]"
                    : "text-[#1a2233] hover:text-[color:var(--brand-primary)] hover:bg-[color:var(--brand-surface-soft)]"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageToggle className="hidden sm:inline-flex" />
          <a
            href={whatsappLink(t.whatsapp.message)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-contact-cta"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm font-display font-semibold rounded-full text-white transition-all duration-200 hover:-translate-y-[1px]"
            style={{ background: "var(--brand-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--brand-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-primary)")}
          >
            <WhatsAppIcon size={14} />
            {t.nav.contactCta}
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label="Toggle navigation"
            data-testid="mobile-menu-btn"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-brand-soft text-[color:var(--brand-primary)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-soft bg-white" data-testid="mobile-menu">
          <div className="container-brand py-4 flex flex-col gap-1">
            {items.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="px-3 py-2.5 text-base font-display font-medium rounded-lg text-[#1a2233] hover:bg-[color:var(--brand-surface-soft)]"
              >
                {item.label}
              </Link>
            ))}
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-brand-soft">
              <LanguageToggle />
              <a
                href={whatsappLink(t.whatsapp.message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-contact-cta"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-display font-semibold rounded-full text-white"
                style={{ background: "var(--brand-primary)" }}
              >
                <WhatsAppIcon size={14} />
                {t.nav.contactCta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
